import { useEffect, useId, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type { ContentSummary } from '@cms/contracts'
import { listContents } from '../api/endpoints.js'
import { useI18n } from '../i18n/context.js'
import { paths } from '../routes.js'
import { useSiteWorkspace } from '../workspace/site.js'
import { Thumb } from '../components/Thumb.js'
import { Icon, IconButton, StatusChip, cx } from '../ui/primitives.js'
import { contentStatusTone } from '../ui/status.js'
import { SITE_ITEMS, matchScreens, type JumpTarget } from './nav.js'

/**
 * "Cerca in tutti i contenuti": `GET /contents?search=` without a site, which
 * the gateway answers across every site the person can read (the repository
 * already filters by tenant and project access). The first results appear
 * under the field; Enter, or the last entry, opens the full list on the
 * current site with the same words.
 */
export function GlobalSearch({ onExpandedChange }: { onExpandedChange?: (expanded: boolean) => void }) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { siteId, sites } = useSiteWorkspace()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<ContentSummary[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const [expanded, setExpanded] = useState(false)
  const input = useRef<HTMLInputElement | null>(null)
  const root = useRef<HTMLDivElement | null>(null)
  const listId = useId()
  const targets = useMemo<JumpTarget[]>(() => [
    { key: 'home', label: t('nav.home'), icon: 'home', to: paths.home() },
    ...(siteId ? [
      { key: 'overview', label: t('nav.overview'), icon: 'dashboard' as const, to: paths.site(siteId) },
      ...(Object.entries(SITE_ITEMS) as [keyof typeof SITE_ITEMS, (typeof SITE_ITEMS)[keyof typeof SITE_ITEMS]][]).map(([name, item]) => ({ key: name, label: t(item.labelKey), icon: item.icon, to: paths.siteSection(siteId, name) })),
    ] : []),
    { key: 'knowledge', label: t('nav.knowledge'), icon: 'psychology', to: paths.knowledge() },
    { key: 'releases', label: t('release.notes'), icon: 'new_releases', to: paths.releaseNotes() },
  ], [t, siteId])
  const trimmed = query.trim()
  const screens = useMemo(() => matchScreens(trimmed, targets), [trimmed, targets])

  useEffect(() => onExpandedChange?.(expanded), [expanded, onExpandedChange])

  // Ctrl+K (⌘K on a Mac) from anywhere: the quick jump to a screen or a content.
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setExpanded(true)
        setOpen(true)
        window.setTimeout(() => { input.current?.focus(); input.current?.select() }, 0)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (trimmed.length < 2) { setResults([]); setTotal(0); setLoading(false); return }
    let cancelled = false
    setLoading(true)
    const timer = window.setTimeout(() => {
      listContents({ search: trimmed, limit: 8 })
        .then((page) => { if (!cancelled) { setResults(page.items); setTotal(page.total); setActive(-1) } })
        .catch(() => { if (!cancelled) { setResults([]); setTotal(0) } })
        .finally(() => { if (!cancelled) setLoading(false) })
    }, 250)
    return () => { cancelled = true; window.clearTimeout(timer) }
  }, [trimmed])

  useEffect(() => {
    if (!open) return
    const onPointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointer)
    return () => document.removeEventListener('pointerdown', onPointer)
  }, [open])

  const siteName = (id: string) => sites.find((site) => site.id === id)?.name ?? ''
  const allHref = siteId ? paths.contents(siteId, { q: trimmed }) : null
  const reset = () => { setOpen(false); setQuery(''); setExpanded(false); input.current?.blur() }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const count = screens.length + results.length + (allHref ? 1 : 0)
    if (event.key === 'Escape') { setOpen(false); if (!query) setExpanded(false); return }
    if (event.key === 'ArrowDown') { event.preventDefault(); setOpen(true); setActive((current) => (current + 1) % Math.max(count, 1)); return }
    if (event.key === 'ArrowUp') { event.preventDefault(); setActive((current) => (current - 1 + count) % Math.max(count, 1)); return }
    if (event.key === 'Enter' && trimmed) {
      event.preventDefault()
      const chosenScreen = screens[active]
      const chosen = results[active - screens.length]
      if (chosenScreen) navigate(chosenScreen.to)
      else if (chosen) navigate(paths.content(chosen.siteId, chosen.id))
      else if (allHref) navigate(allHref)
      reset()
    }
  }

  const showPanel = open && trimmed.length >= 2
  return <div ref={root} className={cx('global-search', expanded && 'is-expanded')} role="search">
    <IconButton icon="search" label={t('search.open')} className="global-search-open" onClick={() => { setExpanded(true); window.setTimeout(() => input.current?.focus(), 0) }}/>
    <div className="global-search-box">
      <IconButton icon="arrow_back" label={t('search.close')} className="global-search-back" onClick={() => { setExpanded(false); setOpen(false) }}/>
      <Icon name="search" className="global-search-icon"/>
      <input
        ref={input}
        type="search"
        value={query}
        placeholder={t('search.placeholder')}
        aria-label={t('search.placeholder')}
        title={t('jump.shortcut')}
        role="combobox"
        aria-expanded={showPanel}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showPanel && active >= 0 ? `${listId}-${active}` : undefined}
        onChange={(event) => { setQuery(event.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {query && <IconButton icon="close" label={t('search.clear')} onClick={() => { setQuery(''); input.current?.focus() }}/>}
    </div>
    {showPanel && <div className="global-search-panel">
      <ul id={listId} role="listbox" aria-label={t('search.results')}>
        {screens.map((item, index) => <li key={`screen-${item.key}`} role="option" id={`${listId}-${index}`} aria-selected={index === active}>
          <Link to={item.to} className={cx('global-search-result', index === active && 'is-active')} onClick={reset}>
            <Icon name={item.icon} size={20}/>
            <span className="global-search-result-text"><strong>{item.label}</strong><small>{t('jump.screens')}</small></span>
          </Link>
        </li>)}
        {loading && results.length === 0 && screens.length === 0 && <li className="global-search-note">{t('search.searching')}</li>}
        {!loading && results.length === 0 && screens.length === 0 && <li className="global-search-note">{t('search.noResults', { query: trimmed })}</li>}
        {results.map((item, rawIndex) => { const index = rawIndex + screens.length; return <li key={item.id} role="option" id={`${listId}-${index}`} aria-selected={index === active}>
          <Link to={paths.content(item.siteId, item.id)} className={cx('global-search-result', index === active && 'is-active')} onClick={reset}>
            <Thumb image={item.mainImage} domain={sites.find((site) => site.id === item.siteId)?.primaryDomain} icon={item.contentTypeKey === 'article' ? 'article' : 'description'} size="sm"/>
            <span className="global-search-result-text">
              <strong>{item.title || item.slug}</strong>
              <small>{[siteName(item.siteId), item.contentTypeKey, item.locale.toUpperCase()].filter(Boolean).join(' · ')}</small>
            </span>
            <StatusChip tone={contentStatusTone(item.status)}>{t(`status.${item.status}`)}</StatusChip>
          </Link>
        </li> })}
        {allHref && <li role="option" id={`${listId}-${screens.length + results.length}`} aria-selected={active === screens.length + results.length}>
          <Link to={allHref} className={cx('global-search-result', 'global-search-all', active === screens.length + results.length && 'is-active')} onClick={reset}>
            <Icon name="search" size={20}/>
            <span className="global-search-result-text"><strong>{total > results.length ? t('search.seeAllCount', { total }) : t('search.seeAll')}</strong><small>{t('search.inSite', { site: siteName(siteId) })}</small></span>
          </Link>
        </li>}
      </ul>
    </div>}
  </div>
}
